import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, Ban, RotateCcw } from "lucide-react";
import BulkActionBar, { SelectCheckbox } from "@/components/BulkActionBar";

const STATUS_LABEL = {
    approved: "Actif",
    disabled: "Désactivé",
    rejected: "Refusé",
    pending: "En attente",
};

export default function AdminMembers() {
    const qc = useQueryClient();
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("all");
    const [selectedIds, setSelectedIds] = useState(() => new Set());

    const { data: users = [], isLoading } = useQuery({
        queryKey: ["admin-users", "all"],
        queryFn: async () => (await api.get("/admin/users")).data,
    });

    const disable = useMutation({
        mutationFn: (id) => api.post(`/admin/users/${id}/disable`),
        onSuccess: () => {
            toast.success("Compte désactivé");
            qc.invalidateQueries({ queryKey: ["admin-users"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const enable = useMutation({
        mutationFn: (id) => api.post(`/admin/users/${id}/enable`),
        onSuccess: () => {
            toast.success("Compte réactivé");
            qc.invalidateQueries({ queryKey: ["admin-users"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const bulkDelete = useMutation({
        mutationFn: (ids) => api.post("/admin/users/bulk-delete", { ids }),
        onSuccess: (res) => {
            const deleted = res.data?.deleted ?? 0;
            const blocked = res.data?.blocked || [];
            if (deleted > 0) toast.success(`${deleted} membre${deleted > 1 ? "s" : ""} supprimé${deleted > 1 ? "s" : ""}`);
            if (blocked.length > 0) {
                const list = blocked.map((b) => `• ${b.raison_sociale} (${b.active_orders} commande${b.active_orders > 1 ? "s" : ""} active${b.active_orders > 1 ? "s" : ""})`).join("\n");
                toast.warning(`${blocked.length} membre${blocked.length > 1 ? "s" : ""} conservé${blocked.length > 1 ? "s" : ""} — commandes actives en cours :\n${list}`, { duration: 8000 });
            }
            setSelectedIds(new Set());
            qc.invalidateQueries({ queryKey: ["admin-users"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return users.filter((u) => {
            if (status !== "all" && u.status !== status) return false;
            if (!q) return true;
            return `${u.prenom} ${u.nom} ${u.email} ${u.raison_sociale}`.toLowerCase().includes(q);
        });
    }, [users, search, status]);

    const toggleSelect = (id, checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
        });
    };
    const allSelected = filtered.length > 0 && filtered.every((u) => selectedIds.has(u.id));
    const toggleAll = () => {
        if (allSelected) setSelectedIds(new Set());
        else setSelectedIds(new Set(filtered.map((u) => u.id)));
    };
    const clearSelection = () => setSelectedIds(new Set());
    const onBulkDelete = () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (window.confirm(`Supprimer définitivement ${ids.length} membre${ids.length > 1 ? "s" : ""} ?\n\nLes membres avec des commandes actives seront ignorés.`)) {
            bulkDelete.mutate(ids);
        }
    };

    return (
        <div data-testid="admin-members">
            <h2 className="font-serif text-2xl text-[#D4BC7C]">Membres</h2>
            <p className="text-sm text-[#A0A0A0] mt-2">Gérez tous les comptes professionnels validés.</p>

            <div className="mt-8 flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D4BC7C] pointer-events-none z-10" size={18} />
                    <input
                        className="bc-input"
                        style={{ paddingLeft: "3rem" }}
                        placeholder="Rechercher par nom, e-mail, société…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="members-search"
                    />
                </div>
                <select className="bc-input md:w-64" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="members-status">
                    <option value="all">Tous les statuts</option>
                    <option value="approved">Actifs</option>
                    <option value="disabled">Désactivés</option>
                    <option value="rejected">Refusés</option>
                    <option value="pending">En attente</option>
                </select>
            </div>

            <div className="mt-6">
                <BulkActionBar
                    selectedCount={selectedIds.size}
                    totalCount={filtered.length}
                    allSelected={allSelected}
                    onToggleAll={toggleAll}
                    onClear={clearSelection}
                    onBulkDelete={onBulkDelete}
                    disabled={bulkDelete.isPending}
                    label="membre"
                />
            </div>

            <div className="mt-4">
                {isLoading && <p className="text-[#A0A0A0]">Chargement...</p>}

                <div className="overflow-x-auto hidden md:block">
                    <table className="bc-table">
                        <thead>
                            <tr>
                                <th style={{ width: 40 }}>
                                    <SelectCheckbox
                                        checked={allSelected}
                                        onChange={toggleAll}
                                        testId="members-select-all"
                                        label="Tout sélectionner"
                                    />
                                </th>
                                <th>Membre</th>
                                <th>Raison sociale</th>
                                <th>Contact</th>
                                <th>Statut</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((u) => (
                                <tr key={u.id} data-testid={`member-row-${u.id}`}>
                                    <td>
                                        <SelectCheckbox
                                            checked={selectedIds.has(u.id)}
                                            onChange={(v) => toggleSelect(u.id, v)}
                                            testId={`select-member-${u.id}`}
                                        />
                                    </td>
                                    <td>
                                        <div className="text-[#F2ECE0]">{u.prenom} {u.nom}</div>
                                        <div className="text-xs text-[#A0A0A0]">{u.fonction}</div>
                                    </td>
                                    <td className="text-[#F2ECE0]">
                                        {u.raison_sociale}
                                        <div className="text-xs text-[#A0A0A0]">{u.ide_tva}</div>
                                    </td>
                                    <td>
                                        <div className="text-[#F2ECE0]">{u.email}</div>
                                        <div className="text-xs text-[#A0A0A0]">{u.telephone}</div>
                                    </td>
                                    <td>
                                        <span
                                            className="text-xs uppercase tracking-widest font-medium px-2 py-1 border"
                                            style={{
                                                color: u.status === "approved" ? "#D4BC7C" : "#A0A0A0",
                                                borderColor: u.status === "approved" ? "var(--bc-gold)" : "var(--bc-border)",
                                            }}
                                        >
                                            {STATUS_LABEL[u.status] || u.status}
                                        </span>
                                    </td>
                                    <td className="text-right">
                                        {u.status === "approved" && (
                                            <button
                                                className="bc-btn bc-btn--sm bc-btn--danger"
                                                onClick={() => disable.mutate(u.id)}
                                                data-testid={`disable-${u.id}`}
                                            >
                                                <Ban size={12} /> Désactiver
                                            </button>
                                        )}
                                        {u.status === "disabled" && (
                                            <button
                                                className="bc-btn bc-btn--sm bc-btn--ghost"
                                                onClick={() => enable.mutate(u.id)}
                                                data-testid={`enable-${u.id}`}
                                            >
                                                <RotateCcw size={12} /> Réactiver
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden space-y-4">
                    {filtered.map((u) => (
                        <div key={u.id} className="bc-card p-5" data-testid={`member-card-${u.id}`}>
                            <div className="flex justify-between items-start gap-3">
                                <div className="flex items-start gap-3">
                                    <SelectCheckbox
                                        checked={selectedIds.has(u.id)}
                                        onChange={(v) => toggleSelect(u.id, v)}
                                        testId={`select-member-mobile-${u.id}`}
                                    />
                                    <div>
                                        <p className="font-serif text-[#D4BC7C] text-lg">{u.prenom} {u.nom}</p>
                                        <p className="text-xs text-[#A0A0A0]">{u.fonction} · {u.raison_sociale}</p>
                                    </div>
                                </div>
                                <span
                                    className="text-[10px] uppercase tracking-widest px-2 py-1 border whitespace-nowrap"
                                    style={{
                                        color: u.status === "approved" ? "#D4BC7C" : "#A0A0A0",
                                        borderColor: u.status === "approved" ? "var(--bc-gold)" : "var(--bc-border)",
                                    }}
                                >
                                    {STATUS_LABEL[u.status] || u.status}
                                </span>
                            </div>
                            <div className="text-sm text-[#F2ECE0] mt-3">{u.email}</div>
                            <div className="text-xs text-[#A0A0A0]">{u.telephone} · {u.ide_tva}</div>
                            <div className="mt-4 flex gap-2">
                                {u.status === "approved" && (
                                    <button className="bc-btn bc-btn--sm bc-btn--danger flex-1" onClick={() => disable.mutate(u.id)}>
                                        Désactiver
                                    </button>
                                )}
                                {u.status === "disabled" && (
                                    <button className="bc-btn bc-btn--sm bc-btn--ghost flex-1" onClick={() => enable.mutate(u.id)}>
                                        Réactiver
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {!isLoading && filtered.length === 0 && (
                    <p className="text-[#A0A0A0] mt-8" data-testid="members-empty">Aucun membre trouvé.</p>
                )}
            </div>
        </div>
    );
}
